'use strict';

const { dwService } = require('amb_base/services/dw.service');

/**
 * Race-condition gate for the "Save to CXP" CTA in the offline-order flow.
 *
 * Disables the Save-to-CXP CTAs (`.configurator-send-offine-order-cta` and
 * `.btn-save-to-cxp`) until `Cart-AddProduct` has succeeded for the variant
 * currently visible on screen. Without this gate, a dealer who clicks Save
 * during the window between page-load and `Cart-AddProduct` completion sends a
 * hybrid PATCH payload to APIM/CXP — URL/code of the new vehicle merged with
 * stale price/state from the previously-viewed vehicle (because
 * `basket.custom.driveAwayPriceResponse` and `basket.custom.location` have not
 * been overwritten yet for the new variant).
 *
 */

const SELECTORS = {
    cta: '.btn-save-to-cxp',
    currentProduct: '#current-product',
    changeModelTriggers: '.btn-change-model, #selectCarModal .redirect-cta'
};
const SUCCESS_CLASS = 'cxp-success';
const DISABLED_CLASS = 'disabled';
const NOTIFY_MESSAGE_KEY = 'configurator.save-to-cxp.calculating.pricing';
const NOTIFY_MESSAGE_BUNDLE = 'product';
const NOTIFY_MESSAGE_FALLBACK = 'Please wait, calculating pricing for this vehicle...';

const saveToCxpGate = {
    /**
     * Tracks which `pid` the basket cache (`driveAwayPriceResponse` + `location`)
     * is currently in sync with. The CTA only enables when this matches the pid
     * of the variant currently rendered on screen.
     *
     * @type {string|null}
     */
    basketSyncedFor: null,

    getCurrentPid: function () {
        return $(SELECTORS.currentProduct).attr('data-selectedvariationid') || null;
    },

    disableCta: function () {
        $(SELECTORS.cta).addClass(DISABLED_CLASS);
    },

    enableCtaIfSynced: function () {
        const pid = this.getCurrentPid();
        const $cta = $(SELECTORS.cta);

        if ($cta.hasClass(SUCCESS_CLASS)) {
            $cta.addClass(DISABLED_CLASS);

            return;
        }

        if (pid && this.basketSyncedFor === pid) {
            $cta.removeClass(DISABLED_CLASS);
        }
    },

    /**
     * Nav Save stays disabled until the basket pid matches, and after a successful save.
     */
    reapplyNavCta: function () {
        var pid = this.getCurrentPid();

        if (pid && this.basketSyncedFor === pid) {
            this.enableCtaIfSynced();
        } else {
            this.disableCta();
        }
    },

    invalidateSync: function () {
        this.basketSyncedFor = null;
        this.disableCta();
    },

    onCartAddProductSuccess: function () {
        const pid = this.getCurrentPid();
        if (pid) {
            this.basketSyncedFor = pid;
            this.enableCtaIfSynced();
        }
    },

    /**
     * Capture-phase click handler — runs BEFORE the bubble-phase jQuery save
     * handler in `configurator.js` so we can stop the AJAX call from firing
     * when the basket cache is not in sync with the visible variant.
     *
     * @param {MouseEvent} event
     */
    onCtaClickCapture: function (event) {
        const target = event.target.closest(SELECTORS.cta);
        if (!target) return;

        const pid = this.getCurrentPid();
        const synced = pid && this.basketSyncedFor === pid;

        if (!synced || target.classList.contains(DISABLED_CLASS)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (window.ecux && window.ecux.notify) {
                const message = dwService.message(NOTIFY_MESSAGE_KEY, NOTIFY_MESSAGE_BUNDLE) || NOTIFY_MESSAGE_FALLBACK;
                window.ecux.notify(message, '');
            }
        }
    },

    setupListeners: function () {
        $('body').on('product:afterAddToCart.saveToCxpGate', () => this.onCartAddProductSuccess());

        $('body').on('product:beforeAttributeSelect.saveToCxpGate', () => this.invalidateSync());

        $(document).on('click.saveToCxpGate', SELECTORS.changeModelTriggers, () => this.invalidateSync());

        document.addEventListener('click', this.onCtaClickCapture.bind(this), true);
    },

    init: function () {
        this.basketSyncedFor = null;
        this.disableCta();
        this.setupListeners();
    }
};

module.exports = saveToCxpGate;
